import { CanDeactivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { SessionGateService } from '../services/auth/session-gate.service';
import { filingSheetScope } from '../utils/navigation-diagnostic-scope.util';
import { FilingFormStateService, FilingFormState } from '../services/mbrs/filing-form-state.service';
import {
  FilingFormContainerComponent,
  FILING_INFO_GATES,
  CROSS_TAB_RESET_NAV_STATE,
  PHASE2_SUBMIT_NAV_STATE,
} from '../components/xbrl-form/filing-form-container/filing-form-container.component';
import { ROUTE_RELOAD_HOP_URL } from '../components/common/route-reload-hop/route-reload-hop.component';

/**
 * Returns true when the target route stays within the filing feature for the
 * SAME conversion. Filing feature paths are `/mbrs/filing`, `/mbrs/filing/role1`,
 * and `/mbrs/filing/role2` (see app.routes.ts) — all three back onto the same
 * conversion's in-memory/localStorage state, so moving between them is treated
 * as intra-conversion navigation and must NOT trigger the leave prompt.
 *
 * Criterion: same conversionId AND pathname is within the filing feature.
 * If the target URL omits the `id` query param, we defer to the pathname check
 * only (the filing sub-routes may be linked without `?id=` in some flows).
 */
const isSameConversionTarget = (
  targetUrl: string,
  currentConversionId: string | null
): boolean => {
  try {
    // URL is route-relative (e.g. "/mbrs/filing?id=abc"); prefix with a dummy
    // origin so URL can parse it reliably across browsers.
    const parsed = new URL(targetUrl, 'http://localhost');
    const path = parsed.pathname;
    const isFilingFeaturePath =
      path === '/mbrs/filing' ||
      path === '/mbrs/filing/role1' ||
      path === '/mbrs/filing/role2';
    if (!isFilingFeaturePath) return false;

    // Only compare conversion ids when the target actually carries one; a
    // missing `?id=` on a filing sub-route is still the same conversion.
    if (!parsed.searchParams.has('id')) return true;
    const targetId = parsed.searchParams.get('id');
    return targetId === (currentConversionId ?? null);
  } catch {
    return false;
  }
};

export const canDeactivateFilingGuard: CanDeactivateFn<FilingFormContainerComponent> = async (
  component,
  _currentRoute,
  _currentState,
  nextState
): Promise<boolean> => {
  const stateService = inject(FilingFormStateService);
  const authService = inject(AuthService);
  const sessionGate = inject(SessionGateService);
  const router = inject(Router);

  // Session-teardown bypass. When the session is being torn down — a forced
  // logout (session expiry/supersession, cross-tab logout, cross-scope login) or
  // a manual sign-out — `AuthService` flips `isAuthenticated` to false (via
  // `clearAuthState()`) BEFORE navigating to `/auth/login`, and that teardown has
  // already wiped the in-memory/cached filing state. Prompting "you have unsaved
  // changes" is then both pointless (the data is already gone) and harmful (it
  // traps the user behind a dialog instead of reaching login), so let the
  // navigation pass straight through.
  //
  // `isAuthenticated() === false` is the reliable signal: it holds for BOTH the
  // forced-logout and manual-logout paths. A manual logout navigates with no
  // `authReason`, so the navigation-state marker alone misses it; the forced
  // logout additionally carries `authReason`, which we still honour as an
  // explicit secondary signal.
  const isForcedLogout = !!router.getCurrentNavigation()?.extras.state?.['authReason'];
  if (isForcedLogout || authService.isAuthenticated() === false) {
    return true;
  }

  // Failed-load Retry bypass — scoped to the Retry hop ONLY. When the
  // conversion-wide load failed (the page shows FilingLoadFailurePanelComponent
  // instead of any sheet), `FilingFormContainerComponent.retryLoad` re-creates
  // the page by hopping through ROUTE_RELOAD_HOP_URL. Any modified state in the
  // store came from an EARLIER session's IndexedDB cache, so prompting on that
  // hop would be misleading and "discard" would destroy real work the user
  // never saw; `CurrentSheetCacheService` likewise ignores the hop, so the
  // cached edits survive the retry.
  //
  // Any OTHER departure from a failed page falls through to the logic below
  // unchanged: leaving the form flushes the caches per
  // `CurrentSheetCacheService`'s leave-the-form policy, so the existing
  // unsaved-changes prompt is the user's only warning and must stay.
  // The hop URL carries no query string or fragment today, so exact match.
  if (component.loadFailure?.() != null && nextState.url === ROUTE_RELOAD_HOP_URL) {
    return true;
  }

  // Cross-tab conversion-reset bypass. When a sibling tab cancels/reverts this
  // conversion, it broadcasts a reset and this (passive) tab is redirected to
  // the listing. The user already confirmed the discard on the origin tab, so
  // re-prompting "you have unsaved changes" here is redundant and would trap
  // the tab on the form. The redirect tags its navigation with this marker.
  const isCrossTabReset = !!router.getCurrentNavigation()?.extras.state?.[CROSS_TAB_RESET_NAV_STATE];
  if (isCrossTabReset) {
    return true;
  }

  // Phase-2 submit bypass. The redirect that follows a SUCCESSFUL Filing
  // Prerequisites (role0) / Filing Information (role1) submit is tagged with
  // this marker. Those two paths commit only their OWN sheet
  // (`commitSheetAsOriginal`), unlike Submit for Validation
  // (`commitModifiedStateAsOriginal`), so `hasAnySheetModified()` below could
  // still be true and prompt "you have unsaved changes" on a submit that had
  // already persisted. Worse, "Stay on this page" cancelled the navigation —
  // and with it the "we are converting…" toast it carried — so the user got no
  // confirmation and could submit a second time. The data question is settled
  // upstream: role0 wipes the other sheets before submitting and role1
  // discards them on success, both because the backend re-extracts every
  // sheet. Nothing is left to warn about here.
  const isPhase2Submit = !!router.getCurrentNavigation()?.extras.state?.[PHASE2_SUBMIT_NAV_STATE];
  if (isPhase2Submit) {
    return true;
  }

  // The guard fires on ANY dirty sheet via hasAnySheetModified(), which
  // already covers both filing-info gate sheets (role0 'filingPrerequisites'
  // and role1 'filingInfo'). The explicit check below documents the intent
  // for the two gate sheets specifically — these are the sheets whose
  // unsaved edits silently affect downstream sheet enablement, so they are
  // the most important to block. Listing them here keeps parity with the
  // navigation-block logic in `onSheetChange` (see `FILING_INFO_GATES`).
  const isAnyGateSheetDirty = FILING_INFO_GATES.some((gate) =>
    stateService.isSheetModified(gate.stateKey as keyof FilingFormState)
  );

  if (!stateService.hasAnySheetModified() && !isAnyGateSheetDirty) {
    return true;
  }

  if (isSameConversionTarget(nextState.url, component.conversionId())) {
    return true;
  }

  // Re-check the session BEFORE prompting (P.O., 2026-10-08): on an ended
  // session the gate has already started forceLogout, whose own navigation
  // passes this guard through the authReason / isAuthenticated() === false
  // bypass above, so the user lands on login with no "Leaving this page?"
  // prompt. Only reached when the prompt would actually show, so a clean
  // leave makes no extra request.
  // The scope names the sheet being left, so the AUTH code reads
  // `MBRS-S<role>-AUTH-<conversion>`.
  if (!(await sessionGate.confirmSessionBeforeLeaving({
    scope: filingSheetScope(component.currentSheet),
    conversionId: component.conversionId(),
  }))) {
    return false;
  }

  const shouldLeave = await firstValueFrom(component.confirmLeave());
  if (shouldLeave) {
    // Per-sheet abandonment, and NOT the whole-form `discardModifiedChanges()`
    // this used to call.
    //
    // The rule is now the same on both ways out of the form — closing the tab
    // and confirming this dialog — namely: the sheet this tab had on screen is
    // released, and it survives only if some other tab is still showing it.
    // Keeping the two paths identical is the whole point; a confirmed
    // "Continue without saving" that discarded more than a tab close did would
    // give the same user action two different blast radii depending on how the
    // user happened to leave.
    //
    // Developer Note (revisit after launch — P.O. acknowledged 2026-08-27):
    // this narrows the whole-form discard scope the P.O. ruled on 2026-08-21,
    // and the consequence is that sheets this tab dirtied and then navigated
    // away from are NOT discarded here. They are collected later by the
    // conversion-wide `shouldDiscardAsLastTabOut` probe on the next open. The
    // worked example and the durable fix are documented at the top of the
    // per-sheet abandonment section in `FilingFormStateService`.
    stateService.discardMountedSheetAndRelease();
  }
  return shouldLeave;
};
